import { useState } from 'react';
import { Star } from 'lucide-react';
import type { ObjectLibraryItem } from './types';
import { formatLength, type LengthUnits } from '@peles/shared';
import { libraryKey, type LibraryPrefs } from './libraryPrefs';

interface LibraryItemListProps {
  items: ObjectLibraryItem[];
  prefs: LibraryPrefs;
  onAdd: (item: ObjectLibraryItem) => void;
  onToggleFavourite: (item: ObjectLibraryItem) => void;
  /** Name each piece's own category — on Favourites and Most used, which mix them. */
  showCategory?: boolean;
  emptyMessage?: string;
  /** How many show before "Show more"; each press shows as many again. */
  pageSize?: number;
  /** How each piece's size reads: `2.5'` or `2' 6"`. */
  lengthUnits?: LengthUnits;
}

export const LIBRARY_PAGE_SIZE = 20;

/**
 * The object library's list of pieces: press one to put it on the plan, or
 * its star to keep it under Favourites. How often this person has used a
 * piece is shown under it once they have.
 */
export function LibraryItemList({
  items,
  prefs,
  onAdd,
  onToggleFavourite,
  showCategory = false,
  emptyMessage,
  pageSize = LIBRARY_PAGE_SIZE,
  lengthUnits = 'ft',
}: LibraryItemListProps) {
  const [shown, setShown] = useState(pageSize);
  if (items.length === 0) {
    return emptyMessage ? (
      <p className="px-1 py-2 text-xs text-gray-400" data-testid="library-empty">
        {emptyMessage}
      </p>
    ) : null;
  }
  return (
    <div className="space-y-1" data-testid="library-items">
      {items.slice(0, shown).map((item, idx) => {
        const key = libraryKey(item);
        const favourite = prefs.favourites.includes(key);
        const uses = prefs.uses[key]?.count ?? 0;
        return (
          <div
            key={`${key}-${idx}`}
            className="flex items-stretch border border-gray-200 rounded hover:bg-blue-50"
          >
            <button
              type="button"
              onClick={() => onAdd(item)}
              className="flex-1 min-w-0 px-2 py-1.5 text-xs text-left"
              data-testid={`lib-item-${item.objectType}-${idx}`}
            >
              <div className="font-medium text-gray-700 truncate">{item.name}</div>
              <div className="text-gray-400">
                {formatLength(item.widthFt, lengthUnits)} x{' '}
                {formatLength(item.heightFt, lengthUnits)}
                {item.seats ? ` | ${item.seats} seats` : ''}
                {showCategory ? ` · ${item.category}` : ''}
              </div>
              {uses > 0 && (
                <div className="text-[10px] text-gray-400" data-testid="lib-item-uses">
                  Used {uses === 1 ? 'once' : `${uses} times`}
                </div>
              )}
            </button>
            <button
              type="button"
              onClick={() => onToggleFavourite(item)}
              aria-pressed={favourite}
              aria-label={
                favourite ? `Remove ${item.name} from favourites` : `Add ${item.name} to favourites`
              }
              title={favourite ? 'Remove from favourites' : 'Add to favourites'}
              className={`px-2 flex items-center ${
                favourite ? 'text-amber-500' : 'text-gray-300 hover:text-amber-500'
              }`}
              data-testid={`lib-fav-${item.objectType}-${idx}`}
            >
              <Star size={14} fill={favourite ? 'currentColor' : 'none'} />
            </button>
          </div>
        );
      })}
      {items.length > shown && (
        <button
          type="button"
          onClick={() => setShown((n) => n + pageSize)}
          className="w-full px-2 py-1 text-xs text-primary-600 hover:underline"
          data-testid="lib-show-more"
        >
          Show {Math.min(pageSize, items.length - shown)} more
        </button>
      )}
    </div>
  );
}
